import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Plus, Pencil, Trash2, Save, X, RefreshCw, ExternalLink, Sparkles, ShieldCheck, ShieldAlert } from "lucide-react";
import {
  fetchWatchProfiles, createWatchProfile, updateWatchProfile,
  deleteWatchProfile, fetchWatchHits, runWatchProfile, fetchTopics,
} from "../lib/api";

function EmptyState({ children, hint }) {
  return (
    <div data-testid="watch-empty-state" className="border border-dashed border-[#27272A] p-8 text-center font-mono">
      <div className="text-zinc-400 text-[13px]">{children}</div>
      {hint ? <div className="text-zinc-600 text-[11px] mt-2">{hint}</div> : null}
    </div>
  );
}

function PillInput({ label, values, onChange, placeholder, max, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    if (values.length >= max) {
      toast.error(`MAX ${max} ENTRIES`);
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
        {label} <span className="text-zinc-700">({values.length}/{max})</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span
            key={v}
            data-testid={`${testId}-pill-${v}`}
            className="inline-flex items-center gap-1.5 px-2 h-6 text-[11px] font-mono border border-[#27272A] bg-[#0F0F0F] text-zinc-200"
          >
            {v}
            <button
              type="button"
              data-testid={`${testId}-remove-${v}`}
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-zinc-500 hover:text-rose-400"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`${testId}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-[#0A0A0A] border border-[#27272A] px-2 h-8 text-[12px] font-mono text-white placeholder:text-zinc-700 focus:border-amber-400 outline-none"
        />
        <button
          type="button"
          data-testid={`${testId}-add`}
          onClick={add}
          disabled={values.length >= max}
          className="px-2 h-8 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-amber-400 hover:text-white disabled:opacity-50"
        >
          ADD
        </button>
      </div>
    </div>
  );
}

function ProfileEditor({ initial, topics, limits, onSave, onCancel, busy }) {
  const [name, setName] = useState(initial?.name || "");
  const [topicSlugs, setTopicSlugs] = useState(initial?.topic_slugs || []);
  const [people, setPeople] = useState(initial?.people || []);
  const [prompt, setPrompt] = useState(initial?.prompt || "");

  const toggleTopic = (slug) => {
    if (topicSlugs.includes(slug)) {
      setTopicSlugs(topicSlugs.filter((s) => s !== slug));
    } else if (topicSlugs.length < limits.topics) {
      setTopicSlugs([...topicSlugs, slug]);
    } else {
      toast.error(`MAX ${limits.topics} TOPICS`);
    }
  };

  const submit = () => {
    if (!name.trim()) { toast.error("NAME REQUIRED"); return; }
    onSave({ name: name.trim(), topic_slugs: topicSlugs, people, prompt: prompt.trim() });
  };

  return (
    <div data-testid="watch-profile-editor" className="border border-[#27272A] bg-[#0F0F0F] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400">
          {initial ? "EDIT_PROFILE" : "NEW_PROFILE"}
        </div>
        <button
          data-testid="watch-editor-cancel"
          onClick={onCancel}
          className="px-2 h-7 text-[10px] font-mono uppercase border border-[#27272A] text-zinc-400 hover:text-white"
        >
          <X size={12} className="inline mr-1" />CANCEL
        </button>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
          NAME <span className="text-zinc-700">({name.length}/{limits.name})</span>
        </div>
        <input
          data-testid="watch-name-input"
          value={name}
          maxLength={limits.name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. AI safety research"
          className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 h-9 text-[13px] font-mono text-white placeholder:text-zinc-700 focus:border-amber-400 outline-none"
        />
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
          TOPICS <span className="text-zinc-700">({topicSlugs.length}/{limits.topics})</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {topics.map((t) => {
            const on = topicSlugs.includes(t.slug);
            return (
              <button
                key={t.slug}
                type="button"
                data-testid={`watch-topic-${t.slug}`}
                onClick={() => toggleTopic(t.slug)}
                className={
                  "px-2 h-7 text-[10px] font-mono uppercase tracking-wider border " +
                  (on
                    ? "border-amber-400 bg-amber-400/10 text-amber-300"
                    : "border-[#27272A] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200")
                }
              >
                {t.name}
              </button>
            );
          })}
        </div>
      </div>

      <PillInput
        label="PEOPLE / ORGANIZATIONS"
        values={people}
        onChange={setPeople}
        placeholder="Demis Hassabis, OpenAI, Yann LeCun…"
        max={limits.people}
        testId="watch-people"
      />

      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
          CUSTOM PROMPT <span className="text-zinc-700">({prompt.length}/{limits.prompt})</span>
        </div>
        <textarea
          data-testid="watch-prompt-input"
          value={prompt}
          maxLength={limits.prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="What should we watch for? e.g. 'Frontier AI capability + safety research, weekly only.'"
          className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-[13px] font-mono text-white placeholder:text-zinc-700 focus:border-amber-400 outline-none resize-none"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
        <button
          data-testid="watch-editor-save"
          onClick={submit}
          disabled={busy}
          className="px-3 h-8 text-[11px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50"
        >
          <Save size={12} className="inline mr-1.5" />
          {busy ? "SAVING…" : "SAVE_PROFILE"}
        </button>
      </div>
    </div>
  );
}

function HitCard({ hit }) {
  return (
    <div data-testid={`watch-hit-${hit.id}`} className="border border-[#27272A] bg-[#0F0F0F] p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[13px] font-mono text-white leading-snug">{hit.title}</div>
        <div className="text-[10px] font-mono text-amber-400 tabnum shrink-0">{hit.score}</div>
      </div>
      <div className="text-[12px] text-zinc-400 leading-relaxed">{hit.summary}</div>
      {hit.match_reason ? (
        <div className="text-[11px] font-mono text-emerald-300/90 border-l-2 border-emerald-400/40 pl-2">
          <Sparkles size={10} className="inline mr-1" />
          {hit.match_reason}
        </div>
      ) : null}
      <div className="flex items-center gap-3 pt-1 text-[10px] font-mono">
        <a
          href={hit.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-zinc-300 hover:text-white"
        >
          {hit.source_verified ? <ShieldCheck size={10} className="text-emerald-400" /> : <ShieldAlert size={10} className="text-zinc-500" />}
          {hit.publisher || "primary"}
          <ExternalLink size={10} />
        </a>
        {hit.secondary_url ? (
          <a
            href={hit.secondary_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-zinc-300 hover:text-white"
          >
            {hit.secondary_verified ? <ShieldCheck size={10} className="text-emerald-400" /> : <ShieldAlert size={10} className="text-zinc-500" />}
            {hit.secondary_publisher || "secondary"}
            <ExternalLink size={10} />
          </a>
        ) : null}
        <span className="ml-auto text-zinc-600">{hit.week_key}</span>
      </div>
    </div>
  );
}

function ProfileCard({ profile, weeklyCap, onEdit, onDelete, onRun, busyRun }) {
  const { data, isLoading } = useQuery({
    queryKey: ["watch-hits", profile.id],
    queryFn: () => fetchWatchHits(profile.id),
    refetchInterval: 60000,
  });
  const hits = data?.hits || [];
  const used = data?.hits_this_week ?? profile.hits_this_week ?? 0;

  return (
    <div data-testid={`watch-profile-${profile.id}`} className="border border-[#27272A] bg-[#0A0A0A]">
      <div className="flex items-center justify-between px-4 h-12 border-b border-[#27272A]">
        <div className="flex items-center gap-3">
          <Eye size={14} className="text-amber-400" />
          <div className="text-[13px] font-mono text-white">{profile.name}</div>
          <span
            data-testid={`watch-count-${profile.id}`}
            className="text-[10px] font-mono text-zinc-500 border border-[#27272A] px-1.5 py-0.5"
          >
            WEEK_USED {used}/{weeklyCap}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`watch-run-${profile.id}`}
            onClick={() => onRun(profile)}
            disabled={busyRun || used >= weeklyCap}
            title={used >= weeklyCap ? "Weekly cap reached" : "Refresh now"}
            className="px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-amber-400 hover:text-white disabled:opacity-40"
          >
            <RefreshCw size={11} className={"inline mr-1 " + (busyRun ? "animate-spin" : "")} />
            REFRESH
          </button>
          <button
            data-testid={`watch-edit-${profile.id}`}
            onClick={() => onEdit(profile)}
            className="px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-amber-400 hover:text-white"
          >
            <Pencil size={11} className="inline mr-1" />EDIT
          </button>
          <button
            data-testid={`watch-delete-${profile.id}`}
            onClick={() => onDelete(profile)}
            className="px-2 h-7 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:border-rose-400 hover:text-white"
          >
            <Trash2 size={11} className="inline mr-1" />DELETE
          </button>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {(profile.topic_slugs || []).map((s) => (
            <span key={s} className="text-[10px] font-mono uppercase tracking-wider text-amber-300 border border-amber-400/40 px-1.5 py-0.5">
              {s}
            </span>
          ))}
          {(profile.people || []).map((p) => (
            <span key={p} className="text-[10px] font-mono text-cyan-300 border border-cyan-400/40 px-1.5 py-0.5">
              @{p}
            </span>
          ))}
        </div>
        {profile.prompt ? (
          <div className="text-[12px] text-zinc-400 italic font-mono border-l-2 border-zinc-700 pl-2">
            “{profile.prompt}”
          </div>
        ) : null}
      </div>
      <div className="border-t border-[#27272A] px-4 py-3 space-y-2 bg-[#0F0F0F]">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          HITS_THIS_WEEK
        </div>
        {isLoading ? (
          <div className="text-[11px] font-mono text-zinc-600">LOADING…</div>
        ) : hits.length === 0 ? (
          <div className="text-[11px] font-mono text-zinc-600">
            No hits yet. Daily sweep runs at 14:00 UTC — or hit REFRESH above.
          </div>
        ) : (
          <div className="space-y-2">
            {hits.map((h) => <HitCard key={h.id} hit={h} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WatchPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | "new" | profile
  const [busyRun, setBusyRun] = useState(false);

  const { data: topics = [] } = useQuery({ queryKey: ["topics"], queryFn: fetchTopics });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["watch-profiles"],
    queryFn: fetchWatchProfiles,
    retry: false,
  });

  const blocked = useMemo(() => {
    const msg = error?.response?.data?.detail || "";
    return error?.response?.status === 403 && msg.includes("watch_list_not_available");
  }, [error]);

  const createMut = useMutation({
    mutationFn: (payload) => createWatchProfile(payload),
    onSuccess: () => {
      toast.success("PROFILE_SAVED");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["watch-profiles"] });
    },
    onError: (e) => toast.error("SAVE_FAILED", { description: e?.response?.data?.detail || e.message }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => updateWatchProfile(id, payload),
    onSuccess: () => {
      toast.success("PROFILE_UPDATED");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["watch-profiles"] });
    },
    onError: (e) => toast.error("UPDATE_FAILED", { description: e?.response?.data?.detail || e.message }),
  });

  const onSave = (payload) => {
    if (editing === "new") return createMut.mutate(payload);
    return updateMut.mutate({ id: editing.id, payload });
  };

  const onDelete = async (profile) => {
    if (!window.confirm(`Delete watch profile "${profile.name}"? This removes its saved hits too.`)) return;
    try {
      await deleteWatchProfile(profile.id);
      toast.success("PROFILE_DELETED");
      qc.invalidateQueries({ queryKey: ["watch-profiles"] });
    } catch (e) {
      toast.error("DELETE_FAILED", { description: e?.response?.data?.detail || e.message });
    }
  };

  const onRun = async (profile) => {
    setBusyRun(true);
    try {
      const r = await runWatchProfile(profile.id);
      if (r.inserted > 0) {
        toast.success(`+${r.inserted} HIT${r.inserted > 1 ? "S" : ""}`, {
          description: `quality_rejected=${r.rejected_quality || 0} · dedup=${r.deduped || 0}`,
        });
      } else if (r.status === "skipped") {
        toast.info("WEEKLY_CAP_REACHED", { description: "Resumes next week." });
      } else {
        toast.info("NO_STRONG_MATCHES", { description: "Try again later or refine your prompt." });
      }
      qc.invalidateQueries({ queryKey: ["watch-hits", profile.id] });
      qc.invalidateQueries({ queryKey: ["watch-profiles"] });
    } catch (e) {
      toast.error("REFRESH_FAILED", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyRun(false);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="watch-page-loading" className="px-6 py-8 font-mono text-zinc-500 text-[12px]">
        LOADING_WATCH_LIST…
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="text-amber-400" size={22} />
          <h1 className="text-2xl font-mono text-white">PERSONAL_WATCH</h1>
        </div>
        <EmptyState hint="Start a trial or pick a paid plan from /pricing to unlock.">
          Personal watch list is available to trial & paid members.
        </EmptyState>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 py-8 font-mono text-rose-400 text-[12px]">
        FAILED_TO_LOAD: {error?.response?.data?.detail || error?.message}
      </div>
    );
  }

  const { profiles = [], cap = 0, weekly_cap = 3, limits = {} } = data || {};
  const atCap = profiles.length >= cap;

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Eye className="text-amber-400" size={22} />
          <h1 className="text-2xl font-mono text-white">PERSONAL_WATCH</h1>
          <span
            data-testid="watch-profiles-count"
            className="text-[10px] font-mono text-zinc-500 border border-[#27272A] px-1.5 py-0.5"
          >
            {profiles.length}/{cap} PROFILES
          </span>
        </div>
        {!editing && (
          <button
            data-testid="watch-new-profile"
            onClick={() => setEditing("new")}
            disabled={atCap}
            className="px-3 h-8 text-[11px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title={atCap ? "Profile cap reached for your plan" : ""}
          >
            <Plus size={12} className="inline mr-1.5" />NEW_PROFILE
          </button>
        )}
      </div>
      <p className="text-[12px] font-mono text-zinc-500 mb-6 max-w-2xl leading-relaxed">
        Pin a few topics, people, or an org you care about — plus a short prompt — and
        we'll surface up to <span className="text-amber-300">{weekly_cap} strong, source-verified hits per week</span>.
        Daily sweep at 14:00 UTC. Hit REFRESH to pull early.
      </p>

      {editing ? (
        <ProfileEditor
          initial={editing === "new" ? null : editing}
          topics={topics}
          limits={{
            topics: limits.topics ?? 5,
            people: limits.people ?? 10,
            prompt: limits.prompt ?? 280,
            name: limits.name ?? 64,
          }}
          onSave={onSave}
          onCancel={() => setEditing(null)}
          busy={createMut.isPending || updateMut.isPending}
        />
      ) : null}

      {!editing && profiles.length === 0 ? (
        <EmptyState hint='Click "NEW_PROFILE" above to save your first watch.'>
          You haven't saved any watch profiles yet.
        </EmptyState>
      ) : null}

      {!editing && profiles.length > 0 ? (
        <div className="space-y-6 mt-6">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              weeklyCap={weekly_cap}
              onEdit={(pr) => setEditing(pr)}
              onDelete={onDelete}
              onRun={onRun}
              busyRun={busyRun}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
